import React from 'react';
import clsx from 'clsx';
import styles from './MagicSignPortal.module.scss';
import useMagicSignPortalLogic from './useMagicSignPortalLogic';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import OTP from '../../common/_custom/OTP/OTP';

/**
 * PUBLIC e-sign portal — unauthenticated, standalone.
 * Document-first flow (GST-magic-link model): the document opens for review immediately;
 * the OTP is requested and entered in a modal only when the signatory clicks Sign/Accept.
 * Branding (logo, company name, consent copy) comes from the /info/{token} payload.
 */

const PortalHeader: React.FC<{ pnoLogo?: string; pnoName?: string; companyName?: string }> = ({ pnoLogo, pnoName, companyName }) => (
    <header className={styles.Header}>
        <div className={styles.HeaderLeft}>
            {pnoLogo ? (
                <img src={pnoLogo} alt={pnoName ?? 'PNO logo'} className={styles.PnoLogo} />
            ) : (
                pnoName && <span className={styles.PnoName}>{pnoName}</span>
            )}
        </div>
        {companyName && <span className={styles.CompanyName}>{companyName}</span>}
    </header>
);

const LoadingStep: React.FC = () => (
    <div className={styles.LoadingWrapper}>
        <LoadingSpinner height="3rem" color="#5b21b6" />
    </div>
);

const UnavailableStep: React.FC<{ infoError: string }> = ({ infoError }) => (
    <div className={styles.Card}>
        <div className={styles.TornDocIllustration} aria-hidden />
        <p className={styles.UnavailableText}>{infoError || 'Oops! Looks like this document is no longer available'}</p>
    </div>
);

/** Full-page document review with floating Download + Sign/Accept actions (Figma). */
const DocumentView: React.FC<{
    documentUrl: string;
    documentLoading: boolean;
    documentError: string;
    documentName?: string;
    actionLabel?: string;
    signed?: boolean;
    onSign: () => void;
    onDownloadSigned: () => void;
}> = ({ documentUrl, documentLoading, documentError, documentName, actionLabel, signed, onSign, onDownloadSigned }) => (
    <div className={styles.DocumentView}>
        {signed && (
            <div className={styles.SignedBanner}>
                <span className={styles.SignedBannerCheck}>&#10003;</span> Document signed successfully
            </div>
        )}
        {documentLoading ? (
            <LoadingStep />
        ) : documentError ? (
            <p className={styles.ErrorMsg}>{documentError}</p>
        ) : (
            <div className={styles.DocumentCanvas}>
                <iframe src={`${documentUrl}#view=FitH&navpanes=0`} title={documentName ?? 'Document'} allowFullScreen />
            </div>
        )}
        <div className={styles.FloatingActions}>
            {signed ? (
                <button className={styles.PrimaryBtn} onClick={onDownloadSigned}>
                    Download Signed Copy
                </button>
            ) : (
                <>
                    <a href={documentUrl} download={documentName ?? 'document.pdf'} className={styles.OutlineBtn}>
                        Download Document
                    </a>
                    <button className={styles.PrimaryBtn} onClick={onSign} disabled={!!documentError || documentLoading}>
                        {actionLabel ?? 'Sign'}
                    </button>
                </>
            )}
        </div>
    </div>
);

/** OTP entry modal, shown over the document when the signatory clicks Sign/Accept. */
const OtpModal: React.FC<{
    signatoryName?: string;
    signatoryEmail?: string;
    actionLabel?: string;
    otp: string;
    otpError: string;
    signError: string;
    cooldownSeconds: number;
    signing: boolean;
    onOtpChange: (val: string) => void;
    onVerify: () => void;
    onResend: () => void;
    onClose: () => void;
}> = ({
    signatoryName,
    signatoryEmail,
    actionLabel,
    otp,
    otpError,
    signError,
    cooldownSeconds,
    signing,
    onOtpChange,
    onVerify,
    onResend,
    onClose,
}) => {
    const [resetOtp, setResetOtp] = React.useState(false);

    const handleResend = () => {
        setResetOtp(true);
        onResend();
    };

    return (
        <div className={styles.ModalOverlay} onClick={onClose}>
            <div className={styles.Modal} onClick={(e) => e.stopPropagation()}>
                {(signatoryName || signatoryEmail) && (
                    <p className={styles.SigningAs}>
                        Signing as{signatoryName ? <strong> {signatoryName}</strong> : ''}
                        {signatoryEmail ? <span className={styles.SigningAsEmail}> ({signatoryEmail})</span> : ''}
                    </p>
                )}
                <h2 className={styles.CardTitle}>Verify your identity</h2>
                <p className={styles.CardSubtitle}>
                    OTP sent to {signatoryEmail ? <strong>{signatoryEmail}</strong> : 'your email'}. Enter it below to{' '}
                    {(actionLabel ?? 'Sign').toLowerCase()}.
                </p>
                {/* Same OTP boxes as the login/Signup OTP screen (shared _custom/OTP component).
                    The boxes are sized in %, so they need a fixed-height container like EnterOTPScreen's. */}
                <div className={styles.OtpBoxWrap}>
                    <OTP setUserOtp={onOtpChange} resetOtp={resetOtp} setResetOtp={setResetOtp} />
                </div>
                {otpError && <p className={styles.ErrorMsg}>{otpError}</p>}
                {signError && <p className={styles.ErrorMsg}>{signError}</p>}
                <div className={styles.OtpActions}>
                    <button className={styles.PrimaryBtn} onClick={onVerify} disabled={otp.length < 6 || signing}>
                        {signing ? 'Signing…' : `Confirm & ${actionLabel ?? 'Sign'}`}
                    </button>
                    <button className={styles.SecondaryLink} onClick={handleResend} disabled={cooldownSeconds > 0}>
                        {cooldownSeconds > 0 ? `Resend OTP in ${cooldownSeconds}s` : 'Resend OTP'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const MagicSignPortal: React.FC = () => {
    const logic = useMagicSignPortalLogic();
    const {
        step,
        info,
        otp,
        otpError,
        signError,
        cooldownSeconds,
        documentUrl,
        documentLoading,
        documentError,
        signing,
        infoError,
        signed,
        handleOtpChange,
        handleResendOtp,
        handleVerifyOtp,
        handleStartSign,
        handleCancelOtp,
        handleDownloadSigned,
    } = logic;

    const showDocument = step === 'document' || step === 'otp';

    return (
        <div className={styles.Portal}>
            <PortalHeader pnoLogo={info?.pnoLogo} pnoName={info?.pnoName} companyName={info?.companyName} />
            <main className={clsx(styles.Main, { [styles.MainDocument]: showDocument })}>
                {step === 'loading' && <LoadingStep />}
                {step === 'unavailable' && <UnavailableStep infoError={infoError} />}
                {showDocument && (
                    <DocumentView
                        documentUrl={documentUrl}
                        documentLoading={documentLoading}
                        documentError={documentError}
                        documentName={info?.documentName}
                        actionLabel={info?.actionLabel}
                        signed={signed}
                        onSign={handleStartSign}
                        onDownloadSigned={handleDownloadSigned}
                    />
                )}
                {step === 'otp' && (
                    <OtpModal
                        signatoryName={info?.signatoryName}
                        signatoryEmail={info?.signatoryEmail}
                        actionLabel={info?.actionLabel}
                        otp={otp}
                        otpError={otpError}
                        signError={signError}
                        cooldownSeconds={cooldownSeconds}
                        signing={signing}
                        onOtpChange={handleOtpChange}
                        onVerify={handleVerifyOtp}
                        onResend={handleResendOtp}
                        onClose={handleCancelOtp}
                    />
                )}
            </main>
        </div>
    );
};

export default MagicSignPortal;
